<script setup lang="ts">
import { computed } from "vue";
import dayjs from "dayjs";
import { Line } from "vue-chartjs";
import { PREVIOUS_YEARS_COLORS } from "@/modules/base_price/utils/metricColors";
import { ChartData, ChartOptions } from "chart.js";
import { formatNumberForScale } from "chartjs-graph";
import BasePricePreviousYearMonthDTO = Modules.BasePrice.Data.PreviousYear.BasePricePreviousYearMonthDTO;

/**
 * How a target's advertising revenue spread over the year, as three lines of
 * each month's share (0-100%) of its year: the year before the plan so far, the one before that, and
 * the average of the two. Shares rather than euros, so years of different
 * size compare on one axis — the question is the shape, not the size.
 *
 * Identity never rests on colour alone: the legend names each line, the
 * average is dotted, and the values sit in a table under it.
 */
const props = defineProps<{
	months: BasePricePreviousYearMonthDTO[];
	/** The years the "current" and "previous" figures are for, as their lines' labels. */
	currentYear: number;
	previousYear: number;
}>();

/**
 * The three series, in a fixed order that also fixes their colour — see
 * PREVIOUS_YEARS_COLORS, shared by every previous-years chart. The average is
 * dotted as its second, colour-free cue: it is derived from the other two,
 * and reads as such.
 */
const SERIES = computed(() => [
	{ key: "current_year_share", valueKey: "current_year", label: String(props.currentYear), color: PREVIOUS_YEARS_COLORS.currentYear, dash: [] as number[] },
	{ key: "previous_year_share", valueKey: "previous_year", label: String(props.previousYear), color: PREVIOUS_YEARS_COLORS.previousYear, dash: [] as number[] },
	{ key: "average_share", valueKey: "average", label: "Average", color: PREVIOUS_YEARS_COLORS.average, dash: [1, 5] },
] as const);

/**
 * The predicted-average colour, for the average's months that
 * have no figure for the current year yet: there the average is not a mean
 * but the previous year carried forward by the trend — a projection, and
 * shown as one.
 */
const PROJECTED_COLOR = PREVIOUS_YEARS_COLORS.averagePredicted;

const isProjected = (index: number) => props.months[index]?.current_year === 0;

/** Short month names, January first — the rows arrive in calendar order. */
const monthLabels = computed(() =>
	props.months.map((_, i) => dayjs().month(i).format("MMM"))
);

const chartData = computed<ChartData<"line">>(() => ({
	labels: monthLabels.value,
	datasets: SERIES.value.map((series) => ({
		label: series.label,
		data: props.months.map((month) => month[series.key]),
		borderColor: series.color,
		backgroundColor: series.color,
		...(series.key === "average_share"
			? {
					// A segment takes the colour of the month it leads into.
					segment: {
						borderColor: (ctx: { p1DataIndex: number }) =>
							isProjected(ctx.p1DataIndex) ? PROJECTED_COLOR : undefined,
					},
					pointBackgroundColor: props.months.map((_, i) =>
						isProjected(i) ? PROJECTED_COLOR : series.color
					),
				}
			: {}),
		borderWidth: 2,
		borderDash: [...series.dash],
		// Round caps turn the short dashes into dots.
		borderCapStyle: "round",
		pointRadius: 4,
		pointHoverRadius: 6,
		// A ring in the surface colour (variables.module.scss: color-surface)
		// keeps overlapping points apart; canvas can't read CSS variables.
		pointBorderColor: "#fbfcfd",
		pointBorderWidth: 2,
		tension: 0.25,
	})),
}));

const pct = (value: number) => `${value.toFixed(1)}%`;

const options = computed<ChartOptions<"line">>(() => ({
	responsive: true,
	maintainAspectRatio: false,
	animation: false,
	// Hovering anywhere in a month shows all three lines for that month.
	interaction: { mode: "index", intersect: false },
	plugins: {
		legend: {
			position: "top",
			align: "end",
			labels: { usePointStyle: true, pointStyle: "line", boxWidth: 24, color: "#2e3239" },
		},
		tooltip: {
			callbacks: {
				label: (item) =>
					` ${item.dataset.label}: ${pct(Number(item.raw))}${
						item.datasetIndex === 2 && isProjected(item.dataIndex) ? " (projected)" : ""
					}`,
			},
		},
		// Registered globally elsewhere in the app; nothing here wants it.
		datalabels: { display: false },
		// Likewise global once anything imports it: these lines show what
		// earlier years did, so their points are not something to drag.
		dragData: false,
	},
	scales: {
		x: {
			grid: { display: false },
			ticks: { color: "#6b7280" },
		},
		y: {
			beginAtZero: true,
			border: { display: false },
			grid: { color: "rgba(46, 50, 57, 0.08)" },
			ticks: { color: "#6b7280", callback: (value) => `${value}%` },
		},
	},
}));

const money = (value: number) => formatNumberForScale("money", value);

/** The year per line: its revenue added up, and its shares — 100% for a full year, less for a part-year. */
const totals = computed(() =>
	SERIES.value.map((series) => ({
		key: series.key,
		revenue: props.months.reduce((sum, month) => sum + month[series.valueKey], 0),
		share: props.months.reduce((sum, month) => sum + month[series.key], 0),
	}))
);
</script>

<template>
	<div>
		<div class="chart-box">
			<Line :data="chartData" :options="options" />
		</div>
		<div
			v-if="months.some((_, i) => isProjected(i))"
			class="d-flex align-center justify-end ga-1 text-caption text-medium-emphasis"
		>
			<span class="legend-line legend-line--dotted" :style="{ borderColor: PROJECTED_COLOR }" />
			Average projected from {{ previousYear }}, where {{ currentYear }} has no figure yet
		</div>


		<!--
			Revenue and share side by side in one cell per line, small and tight,
			so the three years' euros fit beside their percentages without the
			table outgrowing the chart above it.
		-->
		<v-table density="compact" class="values-table mt-3">
			<thead>
				<tr>
					<th>Month</th>
					<th v-for="series in SERIES" :key="series.key" class="text-right">
						<span class="d-inline-flex align-center ga-1">
							<span
								class="legend-line"
								:class="{ 'legend-line--dotted': series.dash.length }"
								:style="{ borderColor: series.color }"
							/>
							{{ series.label }}
						</span>
					</th>
				</tr>
			</thead>
			<tbody>
				<tr v-for="(month, i) in months" :key="i">
					<td>{{ monthLabels[i] }}</td>
					<td v-for="series in SERIES" :key="series.key" class="text-right text-no-wrap">
						{{ money(month[series.valueKey]) }}
						<span class="values-share">{{ pct(month[series.key]) }}</span>
					</td>
				</tr>
			</tbody>
			<tfoot>
				<tr>
					<td>Year</td>
					<td v-for="total in totals" :key="total.key" class="text-right text-no-wrap">
						{{ money(total.revenue) }}
						<span class="values-share">{{ pct(total.share) }}</span>
					</td>
				</tr>
			</tfoot>
		</v-table>
	</div>
</template>

<style scoped lang="scss">
.chart-box {
	position: relative;
	height: 280px;
}

.values-table {
	font-size: 12px;
	font-variant-numeric: tabular-nums;
}

.values-table :deep(th),
.values-table :deep(td) {
	height: 26px !important;
	padding: 0 8px !important;
}

.values-table :deep(tfoot td) {
	font-weight: 600;
	border-top: 1px solid rgba(var(--v-theme-on-surface), 0.12);
}

// The share reads as a note to the euros beside it.
.values-share {
	display: inline-block;
	min-width: 42px;
	margin-left: 4px;
	color: rgba(var(--v-theme-on-surface), 0.6);
	font-weight: 400;
}

.legend-line {
	width: 18px;
	height: 0;
	border-top: 2px solid;
	display: inline-block;
}

.legend-line--dotted {
	border-top-style: dotted;
}
</style>
